import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';
import FormEditor from './FormEditor';
import FullScreenPanel from './FullScreenPanel';
import { Loader2, ClipboardList, FileText, Check, Trash2, Plus, Edit, Eye, EyeOff } from 'lucide-react';

export default function AdminDashboard({ user }) {
  const [forms, setForms] = useState([]);
  const [editingForm, setEditingForm] = useState(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadForms();
    window.addEventListener('formsUpdated', loadForms);
    return () => window.removeEventListener('formsUpdated', loadForms);
  }, []);

  const loadForms = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/forms`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setForms(data);
      }
    } catch (error) {
      console.error('Failed to load forms:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    localStorage.removeItem('flow');
    setTimeout(() => window.dispatchEvent(new Event('flowReload')), 50);
    const id = String(Date.now());
    const newForm = {
      id,
      name: 'Untitled Form',
      description: '',
      fields: [],
      flow: null,
      isPublished: true
    };
    setEditingForm(newForm);
    setShowFormModal(true);
  };

  const handleEdit = (form) => {
    if (form.flow) {
      localStorage.setItem('flow', JSON.stringify(form.flow));
      setTimeout(() => window.dispatchEvent(new Event('flowReload')), 50);
    } else {
      localStorage.removeItem('flow');
      setTimeout(() => window.dispatchEvent(new Event('flowReload')), 50);
    }
    setEditingForm(form);
    setShowFormModal(true);
  };

  const handleSaveForm = async (updatedForm) => {
    let finalForm = { ...updatedForm };

    try {
      const flowRaw = localStorage.getItem('flow');
      if (flowRaw) {
        finalForm.flow = JSON.parse(flowRaw);
      }
    } catch (e) {}

    try {
      const existing = forms.find(f => f.id === finalForm.id);
      if (existing && typeof finalForm.isPublished !== 'boolean') {
        finalForm.isPublished = !!existing.isPublished;
      }
      const method = existing ? 'PUT' : 'POST';
      const url = method === 'PUT'
        ? `${API_BASE_URL}/api/forms/${finalForm.id}`
        : `${API_BASE_URL}/api/forms`;

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(finalForm)
      });

      if (response.ok) {
        setEditingForm(finalForm);
        loadForms();
      }
    } catch (e) {
      console.error('Failed to save form', e);
    }
  };

  const handleDelete = async (formId) => {
    if (!window.confirm('Are you sure you want to delete this form?')) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/forms/${formId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.ok) {
        loadForms();
      }
    } catch (error) {
      console.error('Failed to delete form:', error);
    }
  };

  const handleAttachFlow = (form) => {
    if (form && form.flow) {
      localStorage.setItem('flow', JSON.stringify(form.flow));
    } else {
      localStorage.removeItem('flow');
    }
    setEditingForm(form);
    setShowFormModal(false);
    setTimeout(() => setShowFormModal(true), 100);
  };

  const handleTogglePublish = async (form) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/forms/${form.id}/publish-status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ isPublished: !form.isPublished })
      });

      if (response.ok) {
        loadForms();
      }
    } catch (error) {
      console.error('Failed to update publish status:', error);
    }
  };

  const filteredForms = forms.filter(form =>
    form.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    form.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <Loader2 size={48} className="animate-spin" />
      </div>
    );
  }

  return (
    <div className="forms-dashboard">
      <div className="dashboard-header">
        <div className="dashboard-title">
          <h1>Form Management</h1>
          <p className="dashboard-subtitle">Create and manage approval forms with workflows</p>
        </div>
        <button onClick={handleCreateNew} className="button-primary button-lg">
          <Plus size={16} style={{ marginRight: '4px' }} /> Create New Form
        </button>
      </div>

      <div className="dashboard-filters">
        <input
          type="text"
          placeholder="Search forms..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      {filteredForms.length === 0 ? (
        <div className="empty-state">
          <ClipboardList size={48} />
          <h3>No forms yet</h3>
          <p>Create your first form to get started</p>
          <button onClick={handleCreateNew} className="button-primary button-lg">
            Create Form
          </button>
        </div>
      ) : (
        <div className="forms-grid">
          {filteredForms.map(form => (
            <div key={form.id} className="form-card-new">
              <div className="form-card-header">
                <FileText size={24} />
                <div className="form-card-actions">
                  <button onClick={() => handleEdit(form)} className="action-btn" title="Edit">
                    <Edit size={16} />
                  </button>
                  <button
                    onClick={() => handleTogglePublish(form)}
                    className="action-btn"
                    title={form.isPublished ? 'Unpublish' : 'Publish'}
                  >
                    {form.isPublished ? <Eye size={16} /> : <EyeOff size={16} />}
                  </button>
                  <button onClick={() => handleDelete(form.id)} className="action-btn danger" title="Delete">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              <div className="form-card-body">
                <h3 className="form-card-title">{form.name}</h3>
                <p className="form-card-description">{form.description || 'No description'}</p>

                <div className="form-card-stats">
                  <div className="stat">
                    <span className="stat-label">Fields</span>
                    <span className="stat-value">{form.fields?.length || 0}</span>
                  </div>
                  <div className="stat">
                    <span className="stat-label">Publish</span>
                    <span className="stat-value">
                      {form.isPublished ? (
                        <span className="flow-badge"><Eye size={12} /> Published</span>
                      ) : (
                        <span className="no-flow-badge">Hidden</span>
                      )}
                    </span>
                  </div>
                  <div className="stat">
                    <span className="stat-label">Workflow</span>
                    <span className="stat-value">
                      {form.flow ? (
                        <span className="flow-badge"><Check size={12} /> Configured</span>
                      ) : (
                        <span className="no-flow-badge">Not set</span>
                      )}
                    </span>
                  </div>
                </div>
              </div>

              <div className="form-card-footer">
                <button
                  onClick={() => handleEdit(form)}
                  className="button-primary button-sm"
                  style={{ flex: 1 }}
                >
                  Edit Form
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <FullScreenPanel
        isOpen={showFormModal}
        onClose={() => {
          setShowFormModal(false);
          setEditingForm(null);
        }}
        title={editingForm?.name || 'Create Form'}
        subtitle="Form Builder"
      >
        {editingForm && (
          <FormEditor
            form={editingForm}
            onSave={handleSaveForm}
            onAttachFlow={handleAttachFlow}
          />
        )}
      </FullScreenPanel>
    </div>
  );
}
